import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useState, useMemo } from 'react';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { transformQuillListToNested, nestedListStyles } from '../../../../utils/quillHtmlTransform';
import DropDown from '../../DropDown';
import HoveredIcon from '../../HoveredIconButton';
import HoveredIconConfirm from '../../HoveredIconConfirmButton';
import DataFieldCard from '../DataFieldCard';
import { KnockoutScreenTemplate } from '../../../../../shared/types/Knockout';
import {
  DataFieldExtendedWithSingleLocationAndFollowups,
  DataFieldLocationType,
} from '../../../../../shared/types/DataField';
import { useDeleteKnockoutScreenTemplateMutation } from '../../../../services/knockoutSlice';
import { AddDataFieldModal } from '../../AddDataFieldModal';
import ModalWidget from '../../../ModalWidget/ModalWidget';
import AddDataField from '../../DataFields/AddDataField';

interface KnockoutScreenCardProps {
  knockoutScreen: KnockoutScreenTemplate;
  onEditTriggered: (toEdit: KnockoutScreenTemplate) => void;
  index: number
}

const calculateTotalProps = (
  dataField: DataFieldExtendedWithSingleLocationAndFollowups[]
) => {
  const dataFieldNum = dataField.length;
  let followUpNum = 0;
  dataField.forEach((e) => (followUpNum += e.knockoutFollowups.length));
  return `${dataFieldNum} Data Fields | ${followUpNum} Followups`;
};

const KnockoutScreenCard = ({
  knockoutScreen,
  onEditTriggered,
  index
}: KnockoutScreenCardProps) => {
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [handle, setHandle] = useState<string>('');
  const [originDataFieldEdit, setOriginDataFieldEdit] = useState<
    DataFieldExtendedWithSingleLocationAndFollowups | undefined
  >(undefined);
  const theme = useTheme();
  const [deleteKnockoutScreenTemplate] =
    useDeleteKnockoutScreenTemplateMutation();

  // Transform Quill HTML to proper nested structure
  const transformedDescription = useMemo(
    () => transformQuillListToNested(knockoutScreen.description ?? ''),
    [knockoutScreen.description]
  );

  const handleDelete = () => {
    deleteKnockoutScreenTemplate(knockoutScreen.id);
  };

  const orderedKnockoutScreenFields = [
    ...knockoutScreen.knockoutScreenDataFields,
  ].sort((a, b) => a.dataFieldLocation.order - b.dataFieldLocation.order);
  return (
    <>
      {handle.length ? (
        <AddDataFieldModal
          handle={handle}
          setHandle={setHandle}
          locationID={knockoutScreen.id}
          locationType={DataFieldLocationType.KnockoutScreen}
        />
      ) : null}
      <ModalWidget
        isOpen={!!originDataFieldEdit}
        title="Edit Data Field"
        children={
          <AddDataField
            isClose={originDataFieldEdit === undefined}
            dataField={originDataFieldEdit}
            toggleEdit={() => null}
            onClose={() => setOriginDataFieldEdit(undefined)}
          />
        }
        onClose={() => setOriginDataFieldEdit(undefined)}
      />
      <Box
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : index % 2 == 0 ? '#F3F8FC' :'white'
        }}
      >
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
        >
          <Box alignItems="center">
            <Typography
              component="span"
              sx={{
                fontSize: typographyTokens.fontSizes.base,
                fontWeight: typographyTokens.fontWeights.bold,
              }}
            >
              {knockoutScreen.name}
            </Typography>
            &nbsp;&nbsp;
            {!!orderedKnockoutScreenFields.length && (
              <i
                className={`fa-sharp fa-solid fa-chevron-${
                  showDetail ? 'up' : 'down'
                }`}
                onClick={() => setShowDetail(!showDetail)}
              ></i>
            )}
          </Box>
          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            sx={{
              width: '105px',
            }}
          >
            {/* Delete Button for KnockoutScreen... */}
            <HoveredIconConfirm
              icon="fa-regular fa-trash"
              hoverIcon="fa-solid fa-trash"
              onClick={() => handleDelete()}
            />
            <HoveredIcon
              icon="fa-regular fa-pencil"
              hoverIcon="fa-solid fa-pencil"
              onClick={() => onEditTriggered(knockoutScreen)}
            />
            <DropDown
              icon="fa-regular fa-square-plus"
              hoverIcon="fa-solid fa-square-plus"
              items={['Apply Data Field(s)', 'Add Data Field']}
              onSelect={(e) => setHandle(e)}
            />
          </Stack>
        </Stack>
        <Typography
          component="div"
          sx={{
            fontSize: typographyTokens.fontSizes.xs,
            fontWeight: typographyTokens.fontWeights.bold,
          }}
        >
          {knockoutScreen.starter && 'Starter Screen   '}
          <Typography
            component="span"
            sx={{ fontSize: typographyTokens.fontSizes.xs, color: '#646669' }}
          >
            {calculateTotalProps(orderedKnockoutScreenFields)}
          </Typography>
        </Typography>
        <Typography
          component="div"
          sx={{
            paddingTop: '15px',
            width: 'fit-content',
            maxWidth: '1200px',
            overflowWrap: 'break-word',
            ...nestedListStyles,
          }}
          dangerouslySetInnerHTML={{ __html: transformedDescription }}
        ></Typography>
      </Box>
      <Divider />
      <Box sx={{backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f3f5f7', padding: '0px 30px 0px 30px',}}>
      {showDetail && (
          <Typography
            component="span"
            sx={{
              color: "#2372B9",
              font: "bold 14px/28px Open Sans",
            }}
          >
            DataFields
          </Typography>
        )}
      {showDetail &&
        orderedKnockoutScreenFields.map((dataField, index) => (
          <DataFieldCard
            key={index}
            type={knockoutScreen.starter ? 'Starter' : 'Followup'}
            dataField={dataField}
            datafields={orderedKnockoutScreenFields}
            locationName={knockoutScreen.name}
            locationDescription={knockoutScreen.description ?? ''}
            onEdit={(data) => setOriginDataFieldEdit(data)}
          />
        ))}
      </Box>
    </>
  );
};

export default KnockoutScreenCard;
